import React, { ReactNode } from 'react';
import { FiLock, FiArrowRight, FiZap } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';
import { FeatureName } from '../../types/features';
import FeatureBadge from './FeatureBadge';

interface FeatureGateProps {
  /**
   * Nom de la feature requise
   */
  feature: FeatureName;

  /**
   * Niveau minimum requis (optionnel)
   * Si non spécifié, vérifie juste si la feature est disponible
   */
  minLevel?: string;

  /**
   * Contenu à afficher si la feature est disponible
   */
  children: ReactNode;

  /**
   * Message personnalisé pour la feature bloquée
   */
  lockedMessage?: string;

  /**
   * Affichage en mode "card" (full UI) ou "inline" (simple bouton disabled)
   */
  mode?: 'card' | 'inline' | 'button' | 'page';

  /**
   * Callback quand l'utilisateur clique sur "Upgrader"
   */
  onUpgradeClick?: () => void;

  /**
   * Afficher un aperçu flouté du contenu bloqué
   */
  showBlurredPreview?: boolean;
}

/**
 * Composant "Feature Gate" pour bloquer l'accès à certaines fonctionnalités
 * Affiche un message d'upgrade si la feature n'est pas disponible
 *
 * Usage:
 *
 * Mode Card (full UI avec message):
 *   <FeatureGate feature="export_bicep" mode="card">
 *     <ExportBicepButton />
 *   </FeatureGate>
 *
 * Mode Inline (bouton disabled simple):
 *   <FeatureGate feature="finops_advanced" mode="inline">
 *     <button>FinOps Avancé</button>
 *   </FeatureGate>
 *
 * Mode Button (remplace le bouton par un CTA d'upgrade):
 *   <FeatureGate feature="sso" mode="button" lockedMessage="SSO disponible en ENTREPRISE">
 *     <SSOSettings />
 *   </FeatureGate>
 */
const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  minLevel,
  children,
  lockedMessage,
  mode = 'card',
  onUpgradeClick,
  showBlurredPreview = false
}) => {
  const navigate = useNavigate();
  const { hasFeature, getFeatureLevel, loading } = usePlanFeatures();

  // Skeleton pendant le chargement
  if (loading) {
    return (
      <div className="animate-pulse space-y-4 p-8">
        <div className="h-8 bg-gray-200 rounded w-1/3" />
        <div className="h-4 bg-gray-200 rounded w-2/3" />
        <div className="h-64 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  // Vérifier si la feature est disponible
  const featureLevel = getFeatureLevel(feature);
  const isAvailable = hasFeature(feature);

  // Si niveau minimum requis, vérifier
  let hasRequiredLevel = isAvailable;
  if (isAvailable && minLevel) {
    const levels: Record<string, number> = {
      basic: 1,
      standard: 2,
      advanced: 3,
      limited: 2,
      unlimited: 3,
      retention_30: 2
    };

    const currentLevelValue = typeof featureLevel === 'string' ? (levels[featureLevel] || 0) : (featureLevel ? 1 : 0);
    const minLevelValue = levels[minLevel] || 0;
    hasRequiredLevel = currentLevelValue >= minLevelValue;
  }

  // Si la feature est disponible, afficher le contenu normalement
  if (hasRequiredLevel) {
    return <>{children}</>;
  }

  const handleUpgrade = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      navigate('/settings/subscription');
    }
  };

  const defaultMessage = lockedMessage || `Cette fonctionnalité nécessite un plan supérieur`;

  // Mode PAGE : upgrade wall pleine largeur (pour les pages entières)
  if (mode === 'page') {
    return (
      <div className="flex items-center justify-center min-h-[420px] p-8">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
            <FiLock size={34} className="text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {lockedMessage || 'Fonctionnalité non disponible'}
          </h2>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">
            Cette fonctionnalité n'est pas incluse dans votre plan actuel.
            Passez à un plan supérieur pour y accéder.
          </p>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 mb-6 text-left">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <FiZap size={16} className="text-blue-600" />
              </div>
              <div>
                <FeatureBadge feature={feature} level={false} locked showLabel size="lg" />
                {minLevel && (
                  <p className="text-blue-600 text-xs mt-2">Niveau requis : {minLevel}</p>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={handleUpgrade}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 font-medium text-sm transition-colors"
          >
            <FiArrowRight size={18} />
            Voir les plans & tarifs
          </button>
        </div>
      </div>
    );
  }

  // Mode INLINE : désactiver simplement le children
  if (mode === 'inline') {
    return (
      <div className="relative group">
        <div className="opacity-50 pointer-events-none">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-5 transition cursor-not-allowed">
          <FiLock className="text-gray-400 opacity-0 group-hover:opacity-100 transition" size={24} />
        </div>
      </div>
    );
  }

  // Mode BUTTON : remplacer par un bouton d'upgrade
  if (mode === 'button') {
    return (
      <button
        onClick={handleUpgrade}
        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
      >
        <FiLock size={16} />
        <span>Upgrader pour débloquer</span>
        <FiArrowRight size={16} />
      </button>
    );
  }

  // Mode CARD : affichage complet avec message et CTA
  return (
    <div className="relative rounded-xl overflow-hidden">
      {/* Preview flouté (optionnel) */}
      {showBlurredPreview && (
        <div className="absolute inset-0 filter blur-sm opacity-30 pointer-events-none">
          {children}
        </div>
      )}

      {/* Overlay de blocage */}
      <div className={`bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-dashed border-purple-300 rounded-xl p-8 ${showBlurredPreview ? 'relative z-10' : ''}`}>
        <div className="text-center max-w-md mx-auto">
          {/* Icône */}
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <FiLock className="text-purple-600" size={32} />
          </div>

          {/* Titre */}
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Fonctionnalité Premium
          </h3>

          {/* Message */}
          <p className="text-gray-600 mb-4">
            {defaultMessage}
          </p>

          {/* Badge de la feature */}
          <div className="flex justify-center mb-6">
            <FeatureBadge
              feature={feature}
              level={false}
              locked
              showLabel
              size="lg"
            />
          </div>

          {/* CTA d'upgrade */}
          <button
            onClick={handleUpgrade}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition font-medium shadow-lg"
          >
            <FiZap size={20} />
            <span>Passer à un plan supérieur</span>
            <FiArrowRight size={20} />
          </button>

          {/* Info complémentaire */}
          <p className="text-sm text-gray-500 mt-4">
            Débloquez cette fonctionnalité et bien plus encore
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeatureGate;

/**
 * Composant simplifié pour bloquer un bouton spécifique
 *
 * Usage:
 *   <FeatureButton feature="export_bicep" label="Exporter en Bicep" onClick={handleExport} />
 */
export const FeatureButton: React.FC<{
  feature: FeatureName;
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({
  feature,
  label,
  onClick,
  icon,
  variant = 'primary',
  size = 'md',
  className = ''
}) => {
  const navigate = useNavigate();
  const { hasFeature } = usePlanFeatures();

  const isAvailable = hasFeature(feature);

  const baseClasses = 'inline-flex items-center gap-2 rounded-lg font-medium transition';

  const variantClasses = {
    primary: isAvailable
      ? 'bg-blue-600 text-white hover:bg-blue-700'
      : 'bg-gray-300 text-gray-500 cursor-not-allowed',
    secondary: isAvailable
      ? 'bg-gray-600 text-white hover:bg-gray-700'
      : 'bg-gray-200 text-gray-400 cursor-not-allowed',
    outline: isAvailable
      ? 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
      : 'border-2 border-gray-300 text-gray-400 cursor-not-allowed'
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  };

  if (!isAvailable) {
    return (
      <button
        onClick={() => navigate('/settings/subscription')}
        className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
        title="Fonctionnalité non disponible dans votre plan"
      >
        <FiLock size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} />
        <span>{label}</span>
        <span className="text-xs opacity-75">(Upgrade)</span>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
};
